import React, {useState} from 'react';

import classNames from 'classnames';

import {AutoHeightTitleInput} from 'app/components/general/AutoHeightTitleInput';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {CollapsableFolderPickerForm} from 'app/folders/components/CollapsableFolderPickerForm';
import {FormControlGroup, ShowNonFieldFormErrors} from 'app/general/forms';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {TitleSuggestions} from 'app/openai/components/TitleSuggestions';
import {CollapsableTeamPickerForm} from 'app/teams/components/CollapsableTeamPickerForm';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';
import {capitalizeFirstLetter} from 'app/utils/strings';

import type {ReactNode} from 'react';
import type {
  ContentType,
  FolderSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type Props = {
  onCreate: (name: string, generateContent: boolean) => Promise<unknown>;
  forceDisableConfirm: boolean;
  disabledTooltip?: string;
  ct: ContentType;
  defaultName?: string;
  initialFolder: null | string;
  disableFolder?: boolean;
  teams: Array<TeamSerializedType>;
  disabledTeamText?: (team: TeamSerializedType) => string | undefined;
  onChangeTeams: (teams: Array<TeamSerializedType>) => Promise<unknown>;
  onChangeFolders: (folders: Array<FolderSerializedType>) => Promise<unknown>;
  advancedContent?: ReactNode;
};

export function NewDocumentMetadata({
  onCreate,
  forceDisableConfirm = false,
  disabledTooltip,
  disabledTeamText,
  ct,
  defaultName,
  initialFolder = null,
  disableFolder = false,
  teams,
  onChangeTeams,
  onChangeFolders,
  advancedContent,
}: Props) {
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [title, setTitle] = useState<string | undefined>(defaultName);
  const [isTeamsCollapsed, setIsTeamsCollapsed] = useState<boolean>(true);
  const [isGenerateContent, setIsGenerateContent] = useState<boolean>(false);
  const contentType = getContentType(ct);
  const docType = capitalizeFirstLetter(contentType.name);
  const pluralContentName = {procedure: 'procedures', policy: 'policies'}[ct];

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isCreating || forceDisableConfirm || title == null) {
      return;
    }

    setIsCreating(true);

    const disableCreating = () => setIsCreating(false);

    onCreate(title, isGenerateContent)
      .catch(disableCreating)
      .then(disableCreating);
  };

  return (
    <form onSubmit={handleSubmit}>
      <DocHeaderStructure
        titleContent={
          <h1>
            <FormControlGroup field="name">
              <AutoHeightTitleInput
                value={title}
                className="form-control"
                onChange={setTitle}
                autoFocus={true}
                type="text"
                maxLength={128}
                name="title"
                placeholder={gt('%(docType)s Title', {
                  docType,
                })}
              />
            </FormControlGroup>
          </h1>
        }
        extraContent={
          <>
            {isTeamsCollapsed ? (
              <div className="float-right">
                {ct === 'procedure' || ct === 'policy' ? (
                  <SweetAIButton
                    isCreating={isCreating}
                    forceDisableConfirm={forceDisableConfirm}
                    disabledTooltip={disabledTooltip}
                    setIsGenerateContent={setIsGenerateContent}
                  />
                ) : null}
                <ContinueButton
                  isCreating={isCreating}
                  forceDisableConfirm={forceDisableConfirm}
                  disabledTooltip={disabledTooltip}
                />
              </div>
            ) : null}
            <ShowNonFieldFormErrors />
            <CollapsableTeamPickerForm
              onToggleCollapse={setIsTeamsCollapsed}
              small={false}
              selected={teams}
              onChange={onChangeTeams}
              disabledText={disabledTeamText}
              counterPhrase={(numTeams) =>
                ngt(
                  'The %2$s will be added to %1$d team',
                  'The %2$s will be added to %1$d teams',
                  numTeams,
                  contentType.name
                )
              }
            />
            {!disableFolder && (
              <CollapsableFolderPickerForm
                initialSelectedFolder={initialFolder}
                onChange={onChangeFolders}
              />
            )}
            {!isTeamsCollapsed ? (
              <div className="text-right">
                {ct === 'procedure' || ct === 'policy' ? (
                  <SweetAIButton
                    isCreating={isCreating}
                    forceDisableConfirm={forceDisableConfirm}
                    disabledTooltip={disabledTooltip}
                    setIsGenerateContent={setIsGenerateContent}
                  />
                ) : null}
                <ContinueButton
                  isCreating={isCreating}
                  forceDisableConfirm={forceDisableConfirm}
                  disabledTooltip={disabledTooltip}
                />
              </div>
            ) : null}
            {ct == 'procedure' || ct === 'policy' ? (
              <TitleSuggestions
                contentType={ct}
                contentTypePlural={pluralContentName}
                onClick={setTitle}
              />
            ) : null}
          </>
        }
        advancedContent={advancedContent}
      />
    </form>
  );
}

type ContinueProps = {
  isCreating: boolean;
  forceDisableConfirm: boolean;
  disabledTooltip?: string;
};

function ContinueButton({
  isCreating,
  forceDisableConfirm,
  disabledTooltip,
}: ContinueProps) {
  const confirmClasses = classNames('btn', 'btn-primary', {
    disabled: isCreating || forceDisableConfirm,
  });

  return (
    <LazyTooltip hide={!forceDisableConfirm} tooltip={disabledTooltip}>
      <button type="submit" className={confirmClasses}>
        {isCreating ? gt('Creating') : gt('Continue')}
      </button>
    </LazyTooltip>
  );
}

type SweetAIButtonProps = {
  isCreating: boolean;
  forceDisableConfirm: boolean;
  disabledTooltip?: string;
  setIsGenerateContent: (checked: boolean) => unknown;
};

function SweetAIButton({
  isCreating,
  forceDisableConfirm,
  disabledTooltip,
  setIsGenerateContent,
}: SweetAIButtonProps) {
  const confirmClasses = classNames('btn', 'btn-outline-primary', 'mr-2', {
    disabled: isCreating || forceDisableConfirm,
  });

  const tooltip = forceDisableConfirm
    ? disabledTooltip
    : gt(
        'Use our Sweet Artificial Intelligence (SweetAI) to write this document based on the title'
      );

  return (
    <LazyTooltip tooltip={tooltip}>
      <button
        type="submit"
        className={confirmClasses}
        onClick={() => setIsGenerateContent(true)}
      >
        {isCreating ? gt('Creating') : gt('Write with SweetAI')}
      </button>
    </LazyTooltip>
  );
}
